import { AudioDevice } from './AudioDevice';
import { SoundState } from './SoundFactory';
import { AudioSource } from './AudioSource';
import { AudioResource } from './AudioResource';
import { EventStream } from './EventStreamSubscription';

export class Sound {
  private _device: AudioDevice;
  private _resource: AudioResource;
  private _source: AudioSource | null = null;
  private _loop: boolean = false;
  private _volume: number = 1;
  private _muted: boolean = false;
  private _state: SoundState = SoundState.Stopped;

  public readonly ended = new EventStream<void>();

  constructor(device: AudioDevice, resource: AudioResource) {
    this._device = device;
    this._resource = resource;
  }

  public get loop(): boolean {
    return this._loop;
  }

  public set loop(value: boolean) {
    this._loop = value;

    if (this._source) {
      this._source.setLoop(value);
    }
  }

  public get volume(): number {
    return !this.muted ? this._volume : 0;
  }

  public set volume(value: number) {
    this._volume = value;
    this._source?.setVolume(this.volume);
  }

  public set muted(value: boolean) {
    this._muted = value;
    this._source?.setVolume(this.volume);
  }

  public get muted(): boolean {
    return this._muted;
  }

  public get state(): SoundState {
    return this._state;
  }

  public play(): void {
    if (
      (this.state === SoundState.Stopped && this._resource.data) ||
      (this.state === SoundState.Paused && this._resource.data) ||
      (this.state === SoundState.Playing && this._source && this._source.isEnded)
    ) {
      if (!this._source) {
        this._source = this._device.createSource(this._resource.data!);

        this._source.setLoop(this.loop);
        this._source.setVolume(this.volume);
        this._source.ended.listen(() => this._onSourceEnded());
      }

      if (this.state === SoundState.Paused && this._source && !this._source.isEnded) {
        this._source.stop();
      }

      this._source.play();
      this._state = SoundState.Playing;
    }
  }

  public stop(): void {
    if (this.state === SoundState.Playing || this.state === SoundState.Paused) {
      this._source?.stop();
      this._state = SoundState.Stopped;
    }
  }

  public pause(): void {
    if (this.state === SoundState.Playing) {
      this._source?.pause();
      this._state = SoundState.Paused;
    }
  }

  public resume(): void {
    if (this.state === SoundState.Paused) {
      this._source?.resume();
      this._state = SoundState.Playing;
    }
  }

  public create(): void {
    this._state = SoundState.Stopped;
  }

  public destroy(): void {
    if (this._source) {
      this._device.destroySource(this._source);
      this._source = null;
    }

    this._state = SoundState.Destroyed;
  }

  private _onSourceEnded(): void {
    if (this._loop) {
      return;
    }
    if (this._state === SoundState.Playing) {
      this._state = SoundState.Stopped;
    }
    this.ended.dispatchEvent();
  }
}
